import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { InputLabel, InputStyled, InputWrapper } from '@components/shared/SharedStyles';
import { DEFAULT_KLAVIYO_LIST_ID } from '@constants';
import useTranslate from '@hooks/useTranslate';
import useNewsletter from '@utils/useNewsletter';
import { simpleValidateEmail } from '@utils/validateEmail';
import Spinner from './Spinner';

interface Props {
  location?: string;
  listId?: string;
  textColor?: string;
  buttonText?: string;
  onSubmit?: () => void;
}

const NewsletterForm: React.FC<Props> = ({ location = '', listId = DEFAULT_KLAVIYO_LIST_ID, textColor = Colors.navyBlue, buttonText, onSubmit }) => {
  const { t } = useTranslate();
  const [email, setEmail] = useState('');
  const sendButtonText = buttonText || t('components.send');
  const isValidEmail = useMemo(() => simpleValidateEmail(email), [email]);
  const { addToNewsletter, newsletterLoading, newsletterSuccessful } = useNewsletter(location, listId);

  useEffect(() => {
    if (newsletterSuccessful) {
      setEmail('');
      onSubmit?.();
    }
  }, [newsletterSuccessful]);

  const handleAddToNewsletter = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      addToNewsletter(undefined, email);
    },
    [email]
  );

  const getButtonContent = () => {
    if (newsletterLoading) return <Spinner />;
    if (newsletterSuccessful) return <span>✓</span>;
    return sendButtonText;
  };

  return (
    <Container className="newsletter-form-alt">
      <form onSubmit={handleAddToNewsletter}>
        <InputWrapper>
          <InputLabelStyled>{t('components.your-email')}</InputLabelStyled>
          <InputContainer className="input-container">
            <InputFieldStyled
              placeholder={t('components.email-address-placeholder-2')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoCorrect="off"
              autoCapitalize="none"
              $newsletterSuccessful={newsletterSuccessful}
              $height="50px"
            />
            <StyledButton type="submit" $textColor={textColor} disabled={!isValidEmail}>
              {getButtonContent()}
            </StyledButton>
          </InputContainer>
        </InputWrapper>
      </form>
    </Container>
  );
};

export default NewsletterForm;

interface StyleProps {
  $textColor?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const InputLabelStyled = styled.p`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 150%;
  margin-bottom: 0;
  letter-spacing: 0;
  text-align: left;
  color: inherit;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

interface InputFieldStyledProps {
  $newsletterSuccessful: boolean;
}

const InputFieldStyled = styled(InputStyled)<InputFieldStyledProps>`
  opacity: ${({ $newsletterSuccessful }) => ($newsletterSuccessful ? 0.5 : 1.0)},
  marginBottom: 0,
  borderRadius: 2px 0 0 2px,
`;

const Button = styled.button<StyleProps>`
  background-color: ${Colors.paleGold};
  color: ${(props) => props.$textColor};
  height: 50px;
  width: auto;
  padding: 17px 25px;
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
  border: none;
  white-space: nowrap;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 0 2px 2px 0;

  span {
    font-size: 20px;
  }

  &:hover {
    opacity: 0.7;
  }
`;

const StyledButton = styled(Button)`
  text-transform: uppercase;

  &:disabled {
    opacity: 0.5;
  }
`;
